import { Box, styled, Theme } from '@mui/material';

// Light uses the lowest surface (white), dark the mockup's container surface;
// the fade overlay must match so text dissolves into the field background.
const fieldBackground = (theme: Theme) =>
  theme.palette.mode === 'light'
    ? theme.palette.custom.OV.sys.surfaceContainerLowest
    : theme.palette.custom.OV.sys.surfaceContainer;

export const FieldWrapper = styled(Box, { name: 'ExpandableTextAreaField' })({
  position: 'relative',
  // Contain the pill's z-index to this field: without a local stacking context
  // its `zIndex` competes in the page scroll container and paints over the
  // sticky page header while scrolling.
  isolation: 'isolate',
});

export const TextAreaBase = styled('textarea', {
  name: 'ExpandableTextAreaInput',
  shouldForwardProp: (prop) => prop !== 'expanded' && prop !== 'reservePillSpace',
})<{ expanded?: boolean; reservePillSpace?: boolean }>(({ theme, expanded, reservePillSpace }) => ({
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  margin: 0,
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  borderRadius: '0.625rem',
  backgroundColor: fieldBackground(theme),
  color: theme.palette.custom.OV.sys.onBackground,
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.78125rem',
  lineHeight: 1.6,
  // Reserve bottom space whenever the pill is shown so it never overlaps text
  // (collapsed + scrolling, or expanded with the Show less pill at the bottom).
  padding: reservePillSpace ? '0.75rem 0.875rem 2.5rem' : '0.75rem 0.875rem',
  resize: 'none',
  overflowY: expanded ? 'hidden' : 'auto',
  overflowX: 'hidden',
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.custom.OV.sys.primary,
    boxShadow: `0 0 0 2px color-mix(in srgb, ${theme.palette.custom.OV.sys.primary} 28%, transparent)`,
  },
  '&::placeholder': {
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    opacity: 1,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

export const Fade = styled(Box, { name: 'ExpandableTextAreaFade' })(({ theme }) => ({
  position: 'absolute',
  left: '1px',
  right: '1px',
  bottom: '1px',
  height: '3.25rem',
  borderRadius: '0 0 0.625rem 0.625rem',
  background: `linear-gradient(to bottom, transparent, ${fieldBackground(theme)})`,
  pointerEvents: 'none',
}));

export const MorePill = styled('button', { name: 'ExpandableTextAreaMore' })(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  bottom: '0.625rem',
  transform: 'translateX(-50%)',
  zIndex: 2,
  background: theme.palette.custom.OV.sys.surfaceContainerHighest,
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  borderRadius: '999px',
  color: theme.palette.custom.OV.sys.onSurface,
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.71875rem',
  fontWeight: 600,
  cursor: 'pointer',
  padding: '0.1875rem 0.75rem',
  '&:hover': {
    color: theme.palette.custom.OV.sys.onBackground,
    borderColor: theme.palette.custom.OV.sys.outline,
  },
}));
